import { service } from '@ember/service';
import { Ability } from 'ember-can';
import type AuthzService from 'garaje/services/authz';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureConfigService from 'garaje/services/feature-config';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import { Permission } from 'garaje/utils/ui-permissions';
import mergeWith from 'lodash/mergeWith';

type ReservationAction = 'create' | 'edit' | 'delete';
type ReservationActionAbilities = Record<ReservationAction, boolean>;

const permissionsByAction = {
  create: [
    Permission.DESKS_RESERVATION_CREATE,
    Permission.DESKS_DESK_READ,
    Permission.DESKS_AMENITY_READ,
    Permission.DESKS_NEIGHBORHOOD_READ,
  ],
  edit: [
    Permission.DESKS_RESERVATION_UPDATE,
    Permission.DESKS_DESK_READ,
    Permission.DESKS_AMENITY_READ,
    Permission.DESKS_NEIGHBORHOOD_READ,
  ],
  delete: [
    Permission.DESKS_RESERVATION_DELETE,
    Permission.DESKS_DESK_READ,
    Permission.DESKS_AMENITY_READ,
    Permission.DESKS_NEIGHBORHOOD_READ,
  ],
};

export default class ReservationAbility extends Ability {
  @service declare currentAdmin: CurrentAdminService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare featureConfig: FeatureConfigService;
  @service declare authz: AuthzService;

  get canCreate(): boolean {
    return this.canPerformAction('create');
  }

  get canDelete(): boolean {
    return this.canPerformAction('delete');
  }

  get canEdit(): boolean {
    return this.canPerformAction('edit');
  }

  private canPerformAction(action: ReservationAction): boolean {
    const abilities = this.reservationActionAbilities;
    return abilities[action];
  }

  private get reservationActionAbilities(): ReservationActionAbilities {
    const abilitiesViaPermissions = {
      create: this.authz.hasAllPermissionsAtCurrentLocation(permissionsByAction['create']),
      edit: this.authz.hasAllPermissionsAtCurrentLocation(permissionsByAction['edit']),
      delete: this.authz.hasAllPermissionsAtCurrentLocation(permissionsByAction['delete']),
    };

    const canManageAsDelegate = this.featureConfig.isEnabled('adminConfig.canManageAllReservations');
    const abilitiesAsDelegate = {
      create: canManageAsDelegate,
      edit: canManageAsDelegate,
      delete: canManageAsDelegate,
    };

    return mergeWith(
      abilitiesViaPermissions,
      abilitiesAsDelegate,
      (objValue: boolean, srcValue: boolean) => objValue || srcValue,
    );
  }
}
